/**
 * Единицы длины на экране. Модель всегда в миллиметрах (инвариант 7) — единица меняет только
 * подписи размеров и поля ввода: что показать и как понять введённое число.
 */

export type LengthUnit = "mm" | "cm" | "m" | "in";

interface UnitInfo {
	/** Название в переключателе */
	label: string;
	/** Подпись после числа: «3450 мм», «3,45 м» */
	suffix: string;
	/** Миллиметров в одной единице */
	mm: number;
	/** Знаков после запятой — точность до миллиметра (у дюйма — до десятой) */
	digits: number;
}

export const LENGTH_UNITS: Record<LengthUnit, UnitInfo> = {
	mm: { label: "мм", suffix: "мм", mm: 1, digits: 0 },
	cm: { label: "см", suffix: "см", mm: 10, digits: 1 },
	m: { label: "м", suffix: "м", mm: 1000, digits: 3 },
	in: { label: "дюйм", suffix: "″", mm: 25.4, digits: 1 },
};

export const LENGTH_UNIT_ORDER: LengthUnit[] = ["mm", "cm", "m", "in"];

/** Миллиметры → число в единицах, округлённое до точности единицы. */
export const toLengthUnit = (mm: number, unit: LengthUnit): number => {
	const { mm: size, digits } = LENGTH_UNITS[unit];
	const factor = 10 ** digits;
	return Math.round((mm / size) * factor) / factor;
};

/** Число в единицах → целые миллиметры, как хранит модель. */
export const fromLengthUnit = (value: number, unit: LengthUnit): number =>
	Math.round(value * LENGTH_UNITS[unit].mm);

/** Число в единицах для подписи: запятая вместо точки, без лишних нулей — «3,45», «345». */
export const formatLengthNumber = (mm: number, unit: LengthUnit): string =>
	String(toLengthUnit(mm, unit)).replace(".", ",");
